/**
 * Componente principal de la aplicación
 * Configura el enrutamiento y los providers globales
 * 
 * VERSIÓN INMERSIVA - Diseño tipo Awwwards/Heinz
 * con scrollytelling, motion UI y tipografía gigante
 */

import React from 'react';
import { BrowserRouter as Router, Routes } from 'react-router-dom';
import { CookieProvider } from './context/CookieContext';
import CookieBanner from './components/cookies/CookieBanner';
import ScrollToTop from './components/common/ScrollToTop';
import LanguageSync from './i18n/LanguageSync';
import SeoHead from './i18n/SeoHead';
import EntryLanguageRedirect from './i18n/EntryLanguageRedirect';
import renderLocalizedRoutes from './i18n/localizedRoutes';

// Importar páginas inmersivas
import HomePage from './pages/HomePageImmersive';
import AboutPage from './pages/AboutPageImmersive';
import ProductsPage from './pages/ProductsPageImmersive';
import RetailProductsPage from './pages/RetailProductsPageImmersive';
import HorecaProductsPage from './pages/HorecaProductsPageImmersive';
import RecipesPage from './pages/RecipesPageImmersive';
import RecipeDetailPage from './pages/RecipeDetailPageImmersive';

// Importar otras páginas (mantienen diseño anterior por ahora)
import ProductDetailPage from './pages/ProductDetailPage';
import HorecaProductDetailPage from './pages/HorecaProductDetailPage';
import NewsPage from './pages/NewsPage';
import NewsDetailPage from './pages/NewsDetailPage';
import ContactPage from './pages/ContactPage';
import TransparencyPage from './pages/TransparencyPage';
import OrganizativeInfoPage from './pages/transparency/OrganizativeInfoPage';
import OrganizationalInfoPage from './pages/transparency/OrganizationalInfoPage';
import InstitutionalInfoPage from './pages/transparency/InstitutionalInfoPage';
import EconomicInfoPage from './pages/transparency/EconomicInfoPage';
import ContractsInfoPage from './pages/transparency/ContractsInfoPage';
import SubsidiesInfoPage from './pages/transparency/SubsidiesInfoPage';
import ServiciosProcedimientosPage from './pages/transparency/ServiciosProcedimientosPage';
import CookiesPage from './pages/CookiesPage';
import PrivacyPage from './pages/PrivacyPage';
import LegalPage from './pages/LegalPage';

/**
 * Definiciones de rutas (fuente única). Las rutas se escriben en español y sin
 * prefijo; `renderLocalizedRoutes` genera además las versiones /en, /fr y /pt.
 * @type {import('./i18n/localizedRoutes').RouteDefinition[]}
 */
const routeDefinitions = [
  { path: '/', element: <HomePage /> },
  { path: '/recetas', element: <RecipesPage /> },
  { path: '/recetas/:slug', element: <RecipeDetailPage /> },
  // Alias de la ruta del formulario de envío de recetas (destino del QR impreso).
  // Renderiza la misma página de recetas; el modal se abre solo al detectar esta ruta.
  { path: '/envio-receta', element: <RecipesPage /> },
  { path: '/nosotros', element: <AboutPage /> },
  { path: '/sobre-nosotros', element: <AboutPage /> },
  { path: '/cookies', element: <CookiesPage /> },
  { path: '/privacidad', element: <PrivacyPage /> },
  { path: '/aviso', element: <LegalPage /> },

  // Productos
  { path: '/productos', element: <ProductsPage /> },
  { path: '/productos/retail', element: <RetailProductsPage /> },
  { path: '/productos/retail/:productId', element: <ProductDetailPage /> },
  { path: '/productos/horeca', element: <HorecaProductsPage /> },
  { path: '/productos/horeca/:productId', element: <HorecaProductDetailPage /> },

  // Noticias
  { path: '/noticias', element: <NewsPage /> },
  { path: '/noticias/:slug', element: <NewsDetailPage /> },

  // Contacto
  { path: '/contacto', element: <ContactPage /> },

  // Transparencia
  { path: '/transparencia', element: <TransparencyPage /> },
  { path: '/transparencia/organizativa', element: <OrganizationalInfoPage /> },
  { path: '/transparencia/normativa', element: <OrganizativeInfoPage /> },
  { path: '/transparencia/acceso', element: <InstitutionalInfoPage /> },
  { path: '/transparencia/economica', element: <EconomicInfoPage /> },
  { path: '/transparencia/contratos', element: <ContractsInfoPage /> },
  { path: '/transparencia/ayudas', element: <SubsidiesInfoPage /> },
  { path: '/transparencia/servicios', element: <ServiciosProcedimientosPage /> },
];

/**
 * Componente raíz de la aplicación
 * Configura el Router y los Providers necesarios
 * 
 * NOTA: La HomePage usa el nuevo diseño inmersivo.
 * Las demás páginas se irán actualizando progresivamente.
 * 
 * @returns {JSX.Element} Aplicación completa
 */
function App() {
  return (
    <CookieProvider>
      <Router>
        {/* Scroll al inicio en cada cambio de ruta */}
        <ScrollToTop />
        
        {/* Mantiene idioma de i18next y <html lang> alineados con el prefijo de la URL */}
        <LanguageSync />

        {/* Título, descripción, canonical, hreflang y Open Graph según ruta e idioma */}
        <SeoHead />

        {/* Rutas de la aplicación: español sin prefijo + /en, /fr, /pt (misma lista) */}
        {/* En la entrada, aplica el idioma recordado antes de pintar las rutas */}
        <EntryLanguageRedirect>
          <Routes>{renderLocalizedRoutes(routeDefinitions)}</Routes>
        </EntryLanguageRedirect>

        {/* Banner de cookies global */}
        <CookieBanner />
      </Router>
    </CookieProvider>
  );
}

export default App;
